import { GameEngine, GameEvent, Seat, clone, err } from "../engine.js";
import { RngState, nextInt } from "../rng.js";

/**
 * Ludo — Samra rules v1 (documented in docs/games/ludo.md)
 * - 2–4 players, 4 tokens each. A 6 is needed to leave the yard.
 * - Rolling a 6, capturing, or bringing a token home grants another roll.
 * - Three sixes in a row forfeit the turn.
 * - Landing on an opponent outside a safe square sends it back to the yard.
 * - Exact roll needed to reach home.
 */

export const LUDO_TRACK = 52;
export const LUDO_FINISH = 56; // 0..50 main track, 51..55 home column, 56 home
const SAFE_ABS = new Set([0, 8, 13, 21, 26, 34, 39, 47]);

export interface LudoOptions {
  /** Keep playing after the first winner until ranking is complete. */
  playToEnd: boolean;
}

export interface LudoState {
  n: number;
  quadrant: number[]; // board quadrant per seat
  tokens: number[][]; // -1 yard, 0..56 progress
  turn: Seat;
  phase: "roll" | "move";
  dice: number | null;
  sixes: number;
  finished: Seat[];
  over: boolean;
  options: LudoOptions;
  rng: RngState;
  moveNo: number;
}

export type LudoMove = { type: "roll" } | { type: "move"; token: number };

export type LudoView = Omit<LudoState, "rng"> & { legal: number[] };

const QUADS: Record<number, number[]> = { 2: [0, 2], 3: [0, 1, 2], 4: [0, 1, 2, 3] };

export function ludoAbs(quadrant: number, progress: number): number | null {
  if (progress < 0 || progress > 50) return null;
  return (quadrant * 13 + progress) % LUDO_TRACK;
}

function legalTokens(s: LudoState, seat: Seat): number[] {
  if (s.dice == null) return [];
  const d = s.dice;
  const out: number[] = [];
  s.tokens[seat].forEach((p, i) => {
    if (p === -1) {
      if (d === 6) out.push(i);
    } else if (p < LUDO_FINISH && p + d <= LUDO_FINISH) out.push(i);
  });
  return out;
}

function nextSeat(s: LudoState, from: Seat): Seat {
  for (let k = 1; k <= s.n; k++) {
    const c = (from + k) % s.n;
    if (!s.finished.includes(c)) return c;
  }
  return from;
}

function checkOver(s: LudoState) {
  const remaining = s.n - s.finished.length;
  if ((!s.options.playToEnd && s.finished.length >= 1) || remaining <= 1) {
    s.over = true;
  }
}


/** Computer player. easy = random legal token; medium = heuristic with some noise; hard = heuristic. */
function ludoBotMove(state: LudoState, seat: Seat, level: "easy" | "medium" | "hard"): LudoMove | null {
  if (state.over || state.turn !== seat) return null;
  if (state.phase === "roll") return { type: "roll" };
  const legal = legalTokens(state, seat);
  if (!legal.length) return null;
  // Work on a copy of the RNG so choosing a move never changes the authoritative state.
  const rng = { ...state.rng };
  if (level === "easy") return { type: "move", token: legal[nextInt(rng, 0, legal.length - 1)] };
  if (level === "medium" && nextInt(rng, 1, 100) <= 25) {
    return { type: "move", token: legal[nextInt(rng, 0, legal.length - 1)] };
  }
  const d = state.dice ?? 0;
  const score = (i: number): number => {
    const from = state.tokens[seat][i];
    const to = from === -1 ? 0 : from + d;
    let v = 0;
    if (to === LUDO_FINISH) v += 100;
    if (from === -1) v += 40; // leaving the yard needs a six
    const abs = ludoAbs(state.quadrant[seat], to);
    if (abs != null && SAFE_ABS.has(abs)) v += 25;
    if (abs != null && !SAFE_ABS.has(abs)) {
      for (let o = 0; o < state.n; o++) {
        if (o === seat) continue;
        if (state.tokens[o].some((p) => ludoAbs(state.quadrant[o], p) === abs)) v += 80; // capture
      }
    }
    if (to > 50) v += 10; // home column is always safe
    return v + to / 10;
  };
  const best = legal.reduce((a, b) => (score(b) > score(a) ? b : a));
  return { type: "move", token: best };
}

export const ludo: GameEngine<LudoState, LudoMove, LudoView, LudoOptions> = {
  id: "ludo",
  minPlayers: 2,
  maxPlayers: 4,
  playerCounts: [2, 3, 4],
  defaultOptions: { playToEnd: false },

  init(n, seed, options) {
    if (!QUADS[n]) throw new Error("ludo supports 2-4 players");
    return {
      n,
      quadrant: QUADS[n],
      tokens: Array.from({ length: n }, () => [-1, -1, -1, -1]),
      turn: 0,
      phase: "roll",
      dice: null,
      sixes: 0,
      finished: [],
      over: false,
      options: { ...this.defaultOptions, ...options },
      rng: { s: seed | 0 },
      moveNo: 0,
    };
  },

  currentSeat: (s) => (s.over ? null : s.turn),

  applyMove(state, seat, move) {
    if (state.over) return err("game_over");
    if (seat !== state.turn) return err("not_your_turn");
    const s = clone(state);
    const events: GameEvent[] = [];
    s.moveNo++;

    if (move.type === "roll") {
      if (s.phase !== "roll") return err("must_move");
      const d = nextInt(s.rng, 1, 6);
      s.dice = d;
      events.push({ type: "rolled", seat, value: d });
      if (d === 6) s.sixes++;
      if (s.sixes >= 3) {
        events.push({ type: "three_sixes", seat });
        s.sixes = 0;
        s.dice = null;
        s.turn = nextSeat(s, seat);
        s.phase = "roll";
        return { ok: true, state: s, events };
      }
      if (legalTokens(s, seat).length === 0) {
        events.push({ type: "no_moves", seat });
        s.dice = null;
        if (d !== 6) {
          s.sixes = 0;
          s.turn = nextSeat(s, seat);
        }
        s.phase = "roll";
        return { ok: true, state: s, events };
      }
      s.phase = "move";
      return { ok: true, state: s, events };
    }

    if (move.type === "move") {
      if (s.phase !== "move" || s.dice == null) return err("must_roll");
      const legal = legalTokens(s, seat);
      if (!Number.isInteger(move.token) || !legal.includes(move.token)) return err("illegal_token");
      const d = s.dice;
      const from = s.tokens[seat][move.token];
      const to = from === -1 ? 0 : from + d;
      s.tokens[seat][move.token] = to;
      events.push({ type: "moved", seat, token: move.token, from, to });

      let bonus = d === 6;
      const abs = ludoAbs(s.quadrant[seat], to);
      if (abs != null && !SAFE_ABS.has(abs)) {
        for (let o = 0; o < s.n; o++) {
          if (o === seat) continue;
          s.tokens[o].forEach((p, i) => {
            if (ludoAbs(s.quadrant[o], p) === abs) {
              s.tokens[o][i] = -1;
              bonus = true;
              events.push({ type: "captured", seat, victim: o, token: i, at: abs });
            }
          });
        }
      }
      if (to === LUDO_FINISH) {
        bonus = true;
        events.push({ type: "token_home", seat, token: move.token });
        if (s.tokens[seat].every((p) => p === LUDO_FINISH)) {
          s.finished.push(seat);
          events.push({ type: "player_finished", seat, place: s.finished.length });
          checkOver(s);
          bonus = false;
        }
      }
      s.dice = null;
      s.phase = "roll";
      if (s.over) return { ok: true, state: s, events };
      if (bonus && !s.finished.includes(seat)) {
        s.turn = seat;
      } else {
        s.sixes = 0;
        s.turn = nextSeat(s, seat);
      }
      if (s.turn !== seat) s.sixes = 0;
      return { ok: true, state: s, events };
    }
    return err("unknown_move");
  },

  view(s, seat) {
    const { rng: _rng, ...rest } = s;
    return { ...clone(rest), legal: seat === s.turn ? legalTokens(s, seat) : [] };
  },

  result(s) {
    if (!s.over) return null;
    const progress = (seat: number) =>
      s.tokens[seat].reduce((a, p) => a + Math.max(p, 0), 0);
    const rest = Array.from({ length: s.n }, (_, i) => i)
      .filter((i) => !s.finished.includes(i))
      .sort((a, b) => progress(b) - progress(a));
    const ranking = [...s.finished.map((x) => [x]), ...rest.map((x) => [x])];
    return {
      ranking,
      scores: Array.from({ length: s.n }, (_, i) => progress(i)),
      reason: "finished",
    };
  },

  autoMove(s, seat) {
    if (s.over || s.turn !== seat) return null;
    if (s.phase === "roll") return { type: "roll" };
    const legal = legalTokens(s, seat);
    if (!legal.length) return null;
    // Prefer the most advanced token.
    const best = legal.reduce((a, b) => (s.tokens[seat][b] > s.tokens[seat][a] ? b : a));
    return { type: "move", token: best };
  },
  botMove(s, seat, level) {
    return ludoBotMove(s, seat, level);
  },
};
